import React from 'react';
import { Artifact } from '../../../types/artifact';
import { useTheme } from '../../../contexts/ThemeContext';
import { ArtifactoryMarkdown } from '../../artifactory/ArtifactoryMarkdown';
import { extractMarkdownBody } from '../markdownExtractor';

interface DocumentArtifactViewerProps {
  artifact: Artifact;
  mode?: 'default' | 'formatted' | 'raw';
  // Trailmap L2 (spec §6.1a): downshift user-authored markdown headings
  // by N levels when rendered inside an expanded tile (WCAG 1.3.1).
  headingShift?: number;
}

export const DocumentArtifactViewer: React.FC<DocumentArtifactViewerProps> = ({
  artifact,
  mode = 'default',
  headingShift = 0,
}) => {
  const { darkMode } = useTheme();

  const getDocumentContent = () => {
    if (typeof artifact.content === 'string') return { text: artifact.content };
    return artifact.content;
  };

  const document = getDocumentContent();

  const renderRaw = () => (
    <pre className={`whitespace-pre-wrap font-mono text-sm p-4 rounded border overflow-auto ${
      darkMode
        ? 'bg-gray-900 border-gray-600 text-gray-200'
        : 'bg-gray-50 border-gray-200 text-gray-800'
    }`}>
      {typeof document === 'string' ? document : JSON.stringify(document, null, 2)}
    </pre>
  );

  const renderFormatted = () => {
    if (document.html) {
      return (
        <div
          className={`prose prose-sm max-w-none ${darkMode ? 'prose-invert' : ''}`}
          dangerouslySetInnerHTML={{ __html: document.html }}
        />
      );
    }
    // {markdown|text|body|content: "..."} shapes — produced by the
    // persona-chat create_artifacts pipeline (which wraps non-dict
    // content as {markdown}, see create_artifacts_tool.py:581) plus the
    // typed-resolver paths (decision_log / pulse_digest / handoff_note /
    // mindmap / project_manifest). Render via ArtifactoryMarkdown so
    // embedded ```mermaid fences become actual diagrams on the artifact
    // page, not source text or JSON dumps. Plain-text bodies render fine
    // (markdown is a superset). extractMarkdownBody enforces priority:
    // markdown > text > body > content, so a doc with multiple fields
    // prefers the rendered-markdown version.
    const body = extractMarkdownBody(document);
    if (body) {
      return <ArtifactoryMarkdown content={body} size="large" headingShift={headingShift} />;
    }
    return renderRaw();
  };

  switch (mode) {
    case 'raw': return renderRaw();
    case 'formatted': return renderFormatted();
    default: return renderFormatted();
  }
};
